import { useEffect, useState } from 'react'
import type { Token } from '../lib/types'
import { Button } from './ui'

/**
 * The one and only sight of a raw token.
 *
 * The backend stores a sha256, so this value is unrecoverable once the modal
 * closes. It is therefore never logged, never persisted, and the caller is
 * expected to drop it from state on close — `onClose` exists for exactly that.
 */
export function TokenReveal({ token, onClose }: { token: Token; onClose: () => void }) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token.raw_token ?? '')
      setCopied(true)
    } catch {
      // Clipboard is blocked without a secure context or a user gesture. The value
      // is on screen and selectable, so this is a nuisance, not a failure.
      setCopied(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/50 px-4 backdrop-blur-sm"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="token-reveal-title"
        className="w-full max-w-lg rounded-xl border border-line bg-surface p-6 shadow-pop"
      >
        <h2 id="token-reveal-title" className="text-lg font-semibold text-ink">
          Copy this token now
        </h2>
        <p className="mt-1.5 text-sm text-muted">
          It will never be shown again — only a hash is stored. If you lose it, revoke this
          token and issue a new one.
        </p>

        <div className="mt-4 flex items-stretch gap-2">
          <code className="min-w-0 flex-1 overflow-x-auto rounded-[var(--radius-sh)] border border-line bg-sunken px-3 py-2.5 font-mono text-xs text-ink">
            {token.raw_token}
          </code>
          <Button variant="primary" onClick={() => void copy()} className="shrink-0">
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>

        <dl className="mt-5 space-y-1.5 border-t border-line pt-4 text-xs">
          <Row label="Acts as" value={token.owner} />
          {token.created_by && token.created_by !== token.owner && (
            <Row label="Requested by" value={token.created_by} />
          )}
          <Row label="Prefix" value={token.prefix} />
          {token.scopes?.length ? <Row label="Scopes" value={token.scopes.join(', ')} /> : null}
          <Row
            label="Expires"
            value={token.expires_at ? new Date(token.expires_at).toLocaleDateString() : 'never'}
          />
        </dl>

        <div className="mt-6 flex justify-end">
          <Button autoFocus onClick={onClose}>
            I have copied it
          </Button>
        </div>
      </div>
    </div>
  )
}

const Row = ({ label, value }: { label: string; value?: string }) => (
  <div className="flex gap-2">
    <dt className="w-28 shrink-0 text-subtle">{label}</dt>
    <dd className="truncate font-mono text-ink">{value ?? '—'}</dd>
  </div>
)
